import type { ReactNode } from "react";
import { ArrowRight, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import type { QueueEntry } from "./assessment";
import type { Decision } from "../../hooks/useCatalogSession";
import { BuiltBadge, CenteredCard, FitMeter, InPlanBadge, primaryButton, secondaryButton } from "./ui";

// Offsets for the fanned stack: [x px, y px, degrees, scale], front card first.
const FAN = [[0, 0, 0, 1], [34, -18, 3.5, 0.95], [64, -34, 7, 0.9], [90, -48, 10, 0.85]];

/** "Your queue": the stack to step through, what the visitor has done with it, and the answers behind it. */
export default function QueueHub({ queue, decided, viewed, fanned, onStart, onPlan, onRestart, answers }: {
  queue: QueueEntry[];
  decided: Record<string, Decision>;
  viewed: string[];
  fanned: boolean;
  onStart: () => void;
  onPlan: () => void;
  onRestart: () => void;
  answers: ReactNode;
}) {
  const active = queue.filter((e) => decided[e.listing.id] !== "skipped");
  const added = queue.filter((e) => decided[e.listing.id] === "added").length;
  const skipped = queue.filter((e) => decided[e.listing.id] === "skipped").length;
  const seen = queue.filter((e) => viewed.includes(e.listing.id)).length;
  const allSkipped = queue.length > 0 && skipped === queue.length;

  return (
    <div className="flex flex-col gap-12">
      {allSkipped ? (
        <CenteredCard id="skipped-title" icon={<RotateCcw className="h-7 w-7" />} title="You skipped everything in your queue"
                      actions={<>
                        <Link to="/book" className={primaryButton}>Book a call to design something together</Link>
                        <button type="button" onClick={onRestart} className={secondaryButton}>Go through my queue again</button>
                      </>}>
          <p>That's useful to know. Change an answer below to see new ideas, or let's design something together on a short call.</p>
        </CenteredCard>
      ) : (
        <div className="flex flex-wrap items-start gap-8">
          <div className="flex min-w-0 flex-[999_1_540px] flex-col gap-5">
              <div className="relative h-[300px] sm:h-[380px]">
                {active.slice(0, 4).map((entry, i) => {
                  const [x, y, rotate, scale] = fanned ? FAN[i] : FAN[0];
                  return (
                    <div key={entry.listing.id} aria-hidden={i > 0}
                         style={{ zIndex: 10 - i, transform: `translate(${x}px, ${y}px) rotate(${rotate}deg) scale(${scale})` }}
                         className={`absolute bottom-0 left-0 right-10 flex h-[248px] origin-bottom flex-col gap-2.5 overflow-hidden rounded-2xl border border-primary/20 p-5 shadow-xl motion-safe:transition-transform motion-safe:duration-700 sm:right-28 sm:h-[300px] sm:p-8 ${
                           i === 0 ? "bg-white" : "bg-surfaceSoft"}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-text-muted">{queue.indexOf(entry) + 1} of {queue.length}</span>
                        {entry.listing.temporary && <BuiltBadge />}
                        {decided[entry.listing.id] === "added" && <InPlanBadge />}
                      </div>
                      <p className="font-display text-2xl font-bold text-text-primary sm:text-3xl">{entry.listing.name}</p>
                      <p className="max-w-lg text-lg text-text-secondary">{entry.listing.headline}</p>
                      <div className="mt-auto"><FitMeter fit={entry.fit} /></div>
                    </div>
                  );
                }).reverse()}
              </div>
              <button type="button" onClick={onStart} className={`${primaryButton} w-full py-5 text-xl`}>
                {seen ? "Continue exploring your queue" : "Start exploring your queue"}
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </button>
              {active.some((e) => e.listing.temporary) && (
                <p className="flex flex-wrap items-center gap-2 text-sm text-text-muted">
                  <BuiltBadge />Nothing ready-made fit perfectly, so we put this one together from your answers.
                </p>
              )}
          </div>

        <aside aria-labelledby="activity-title" className="catalog-enter flex min-w-0 flex-[1_1_280px] flex-col gap-4 rounded-2xl border border-borderSubtle bg-surface p-6 shadow-sm">
          <h2 id="activity-title" className="font-display text-lg font-bold text-text-primary">Queue activity</h2>
          <dl className="catalog-stagger grid grid-cols-3 gap-2">
            {[["Viewed", seen], ["Added", added], ["Skipped", skipped]].map(([label, value]) => (
              <div key={label} className="flex flex-col-reverse gap-0.5 rounded-xl bg-surfaceSoft p-3">
                <dt className="text-xs font-semibold text-text-muted">{label}</dt>
                <dd key={value} className={`catalog-pop font-display text-3xl font-bold ${label === "Added" ? "text-primary" : "text-text-primary"}`}>{value}</dd>
              </div>
            ))}
          </dl>
          <button type="button" onClick={onPlan} className={secondaryButton}>View my plan</button>
          <p className="text-sm text-text-muted">Applications you add show up in your plan. We'll go over pricing with you on a short call.</p>
        </aside>
        </div>
      )}

      <div role="region" aria-labelledby="answers-title" className="flex flex-col gap-4">
        <div>
          <h2 id="answers-title" className="font-display text-2xl font-bold text-text-primary">Your answers</h2>
          <p className="text-text-muted">These shaped your queue. Change any of them and we'll rebuild it.</p>
        </div>
        {answers}
      </div>
    </div>
  );
}
